import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { BaladaAppService } from '@/services';
import { PostBaladaAppRequest, PutBaladaAppRequest } from '@/services/request/balada-app';
import { BaladaAppConverter } from '@/converters';

export const useBaladaApp = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_BALADA_APP', params],
    async () => {
      const [err, data] = await BaladaAppService.getBaladaApps(params);
      if (err) {
        throw err;
      }
      return {
        data: BaladaAppConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useGetBaladaAppByUUID = () => {
  return useMutation({
    mutationKey: ['GET_BALADA_APP_BY_UUID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await BaladaAppService.getBaladaAppByUUID(uuid);
      if (err) {
        throw err;
      }
      return BaladaAppConverter.toDomainFromByIdResponse(data);
    },
  });
};

export const usePostBaladaAppMutation = () => {
  return useMutation({
    mutationKey: ['POST_BALADA_APP'],
    mutationFn: async (payload: PostBaladaAppRequest) => {
      const [err, data] = await BaladaAppService.postBaladaApp(payload);
      if (err) {
        throw err;
      }
      return BaladaAppConverter.toDomainFromPostResponse(data);
    },
  });
};

type UsePutBaladaAppMutationParams = {
  uuid: string;
  payload: PutBaladaAppRequest;
};

export const usePutBaladaAppMutation = () => {
  return useMutation({
    mutationKey: ['PUT_BALADA_APP'],
    mutationFn: async ({ uuid, payload }: UsePutBaladaAppMutationParams) => {
      const [err, data] = await BaladaAppService.putBaladaApp(uuid, payload);
      if (err) {
        throw err;
      }
      return BaladaAppConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteBaladaAppMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_BALADA_APP'],
    mutationFn: async (uuid: string) => {
      const [err] = await BaladaAppService.deleteBaladaApp(uuid);
      if (err) {
        throw err;
      }
      return true;
    },
  });
};
